import React, { useMemo } from "react";
import Button from "components/button";
import Icon from "components/icon";
import Typography from "components/typography";
import { Table, Columns } from "components/table";
import Pagination from "components/table/pagination";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppTranslation } from "locale/useAppTranslation";
import { dateFormatter } from "_helper/timeFormatter";

import useDriverDetail, { TransportOrderRow } from "./useDriverDetail";

import DriverDetailCard from "./components/driver-detail-card";
import DriverDocumentTable from "./components/driver-document-table";
import DriverHistorySidebar from "./components/driver-history-sidebar";
import { buildDriverHistoryEntry } from "./hooks/useDriverHistory";
import {
  editDriverPageRouteName,
} from "../constants/routes";

export const detailDriverPageRouteName = (id = ":id") =>
    `/master-data/driver/${id}/detail`;

const DriverDetailPage = () => {
    const { t } = useAppTranslation("driver");
    const { t: tHistory } = useAppTranslation("historyDataSidebar");
    const navigate = useNavigate();
    const location = useLocation();

    const {
        id,
        detail,
        documents,
        auditLogs,
        isHistoryOpen,
        setIsHistoryOpen,
        activeTab,
        setActiveTab,
        isLoading,
        transportOrders,
        isTOHistoryLoading,
        isTOHistoryFetching,
        toErrorMessage,
        toPage,
        toLimit,
        toTotalPages,
        handleTOPageChange,
        handleTOLimitChange,
    } = useDriverDetail();

    const transportOrderColumns: Columns<TransportOrderRow>[] = useMemo(
        () => [
            {
                id: "no",
                fieldId: "index",
                label: t("col-no") || "No.",
                width: "60px",
                dragDisabled: true,
            },
            {
                id: "uo-number",
                fieldId: "wo_number",
                label: t("col-uo-number") || "UO Number",
                dragDisabled: true,
                render: (row) => row.wo_number || "—",
            },
            {
                id: "customers",
                fieldId: "customer_name",
                label: t("col-customers") || "Customers",
                dragDisabled: true,
                render: (row) => row.customer_name || "—",
            },
            {
                id: "date",
                fieldId: "date",
                label: t("col-date") || "Date",
                dragDisabled: true,
                render: (row) => dateFormatter(row.date),
            },
            {
                id: "fund-transferred",
                fieldId: "fund_transferred",
                label: t("col-fund-transferred") || "Fund Transferred",
                dragDisabled: true,
                render: (row) => {
                    if (row.fund_transferred == null) return "—";
                    return `Rp ${new Intl.NumberFormat("id-ID").format(Math.ceil(Number(row.fund_transferred)))}`;
                },
            },
            {
                id: "status",
                fieldId: "status",
                label: t("col-status") || "Status",
                dragDisabled: true,
                render: (row) => {
                    if (!row.status) return <span className="text-text-tertiary">—</span>;
                    return <span className="capitalize">{row.status.replace(/_/g, " ")}</span>;
                },
            },
        ],
        [t]
    );

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-16">
                <Typography variant="bodySmall" tone="muted">
                    {t("loading") || "Loading..."}
                </Typography>
            </div>
        );
    }

    if (!detail) {
        return (
            <div className="flex items-center justify-center py-16">
                <Typography variant="bodySmall" tone="muted">
                    {t("data-not-found") || "Data not found"}
                </Typography>
            </div>
        );
    }

    return (
        <>
            <div className="flex flex-col gap-6 print-friendly-layout">
                {/* Top Action Row */}
                <div className="flex items-center justify-between">
                    <Typography variant="heading4" as="h2">
                        {t("detail-section-title") || "DRIVER DETAILS"}
                    </Typography>
                    <div className="flex items-center gap-3 no-print">
                        <Button
                            variant="outline"
                            size="small"
                            iconLeft={<Icon name="clock" size={14} />}
                            onClick={() => setIsHistoryOpen(true)}
                        >
                            {t("btn-history-data") || "History Data"}
                        </Button>
                        <Button
                            variant="outline"
                            size="small"
                            iconLeft={<Icon name="export" size={14} />}
                            onClick={() => window.print()}
                        >
                            {t("btn-export") || "Export"}
                        </Button>
                        <Button
                            variant="fill"
                            color="primary"
                            size="small"
                            iconLeft={<Icon name="edit-2" size={14} />}
                            onClick={() =>
                                navigate(editDriverPageRouteName(id), { state: { from: location.state?.from === "draft" ? "draft" : "detail" } })
                            }
                        >
                            {t("btn-edit") || "Edit"}
                        </Button>
                    </div>
                </div>

                {/* Driver Profile Card */}
                <DriverDetailCard detail={detail} />

                {/* Tab Section */}
                <section className="rounded-2xl border border-border bg-white shadow-sm print-friendly-layout">
                    {/* Print-only Tab Header */}
                    <div className="hidden print:block mb-4 pt-4 px-6 border-b border-border pb-2 page-break-inside-avoid">
                        <Typography variant="heading5" className="font-bold text-text-primary">
                            {activeTab === "transport-order" ? (t("tab-order-history") || "Order History") : (t("tab-document-list") || "Document List")}
                        </Typography>
                    </div>

                    {/* Tab Headers */}
                    <div className="flex border-b border-border no-print">
                        <button
                            type="button"
                            onClick={() => setActiveTab("transport-order")}
                            className={`relative flex items-center gap-2 px-6 py-4 text-sm font-semibold transition-colors ${
                                activeTab === "transport-order"
                                    ? "text-primary-600"
                                    : "text-text-secondary hover:text-text-primary"
                            }`}
                        >
                            <Icon
                                name="paper-double"
                                size={18}
                                className={activeTab === "transport-order" ? "text-primary-600" : "text-text-secondary"}
                            />
                            {t("tab-order-history") || "Order History"}
                            {activeTab === "transport-order" && (
                                <span className="absolute bottom-0 left-0 h-0.5 w-full bg-primary-600" />
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab("document-list")}
                            className={`relative flex items-center gap-2 px-6 py-4 text-sm font-semibold transition-colors ${
                                activeTab === "document-list"
                                    ? "text-primary-600"
                                    : "text-text-secondary hover:text-text-primary"
                            }`}
                        >
                            <Icon
                                name="folder-double"
                                size={18}
                                className={activeTab === "document-list" ? "text-primary-600" : "text-text-secondary"}
                            />
                            {t("tab-document-list") || "Document List"}
                            {activeTab === "document-list" && (
                                <span className="absolute bottom-0 left-0 h-0.5 w-full bg-primary-600" />
                            )}
                        </button>
                    </div>

                    {/* Tab Content */}
                    <div className="p-6">
                        {activeTab === "transport-order" && (
                            <div className="flex flex-col gap-4">
                                <Table<TransportOrderRow>
                                    columns={transportOrderColumns}
                                    data={transportOrders}
                                    rowKey="id"
                                    loading={isTOHistoryLoading || isTOHistoryFetching}
                                    error={toErrorMessage}
                                    currentPage={toPage}
                                    limit={toLimit}
                                    emptyIcon={<Icon name="uo-empty" width={80} height={63} />}
                                    emptyTitle={t("empty-title") || "No Data added yet"}
                                    emptyDescription={t("empty-order-history-description") || "There is no transport order history for this Driver"}
                                    enableColumnDrag={false}
                                    enableColumnResize={false}
                                />
                                {toTotalPages > 1 && (
                                    <div className="flex items-center justify-between pt-2">
                                        <div className="flex items-center gap-2 text-sm text-text-secondary">
                                            <span>{t("rows-per-page") || "Rows per page :"}</span>
                                            <select
                                                value={toLimit}
                                                onChange={(e) => handleTOLimitChange(Number(e.target.value))}
                                                className="rounded-lg border border-border px-2 py-1 text-sm outline-none"
                                            >
                                                <option value={5}>5</option>
                                                <option value={10}>10</option>
                                                <option value={20}>20</option>
                                                <option value={50}>50</option>
                                            </select>
                                        </div>
                                        <Pagination
                                            currentPage={toPage}
                                            totalPages={toTotalPages}
                                            onPageChange={handleTOPageChange}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === "document-list" && (
                            <DriverDocumentTable documents={documents} />
                        )}
                    </div>
                </section>

                {/* Print-only Audit Logs Section */}
                <div className="hidden print:block mt-8 pt-6 border-t border-greyScale-90 page-break-inside-avoid">
                    <Typography variant="heading5" className="font-bold text-text-primary mb-4">
                        {t("history-title") || "Audit Logs"}
                    </Typography>
                    {auditLogs && auditLogs.length > 0 ? (
                        <div className="flex flex-col gap-4">
                            {auditLogs.map((log: any, idx: number) => {
                                const historyItem = buildDriverHistoryEntry(log, tHistory);
                                return (
                                    <div key={log.id || idx} className="py-3 border-b border-greyScale-95 text-sm flex flex-col gap-1 page-break-inside-avoid">
                                        <div className="flex justify-between items-start gap-4">
                                            <div>
                                                <span className="font-bold text-text-primary">{historyItem.actorName}</span>
                                                <span className="text-greyScale-60 mx-2">•</span>
                                                <span className="inline-flex items-center rounded-full bg-greyScale-95 px-2 py-0.5 text-xs font-semibold text-greyScale-60 capitalize">
                                                    {historyItem.actionLabel}
                                                </span>
                                            </div>
                                            <span className="text-xs text-greyScale-50">{historyItem.timestampLabel}</span>
                                        </div>
                                        {historyItem.fieldChanges && historyItem.fieldChanges.length > 0 && (
                                            <ul className="list-disc pl-4 space-y-1 text-text-secondary mt-1">
                                                {historyItem.fieldChanges.map((change, changeIdx) => (
                                                    <li key={changeIdx}>
                                                        {change.sentence ||
                                                            `${change.fieldLabel}: ${change.oldValue ?? "—"} → ${change.newValue ?? "—"}`}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                        {historyItem.reason && (
                                            <div className="text-text-secondary mt-1">
                                                {tHistory("reason") || "Reason"}: {historyItem.reason}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <Typography variant="body" tone="muted">{t("history-empty-title") || "No History Data"}</Typography>
                    )}
                </div>
            </div>

            {/* History Sidebar */}
            <DriverHistorySidebar
                open={isHistoryOpen}
                onClose={() => setIsHistoryOpen(false)}
                driverId={id}
                detail={detail}
            />
        </>
    );
};

export default DriverDetailPage;